import { useState } from 'react';
import { useNavigate, useOutletContext } from 'react-router-dom';
import { AuthCard, TermsContent } from '@legacyorb/ui';
import { Button } from '@/components/ui/button';
import { Loader2, FileText } from 'lucide-react';
import { apiClient } from '@/services/api-client';
import type { User } from '@/types';

interface OnboardingContext {
  user: User;
  setUser: (user: User) => void;
}

export function TermsPage() {
  const navigate = useNavigate();
  const { user, setUser } = useOutletContext<OnboardingContext>();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  const handleAccept = async () => {
    setError('');
    setIsSubmitting(true);

    try {
      const response = await apiClient.post<{ termsAccepted: boolean }>(
        '/api/v1/onboarding/accept-terms'
      );

      if (response.success) {
        setUser({ ...user, termsAcceptedAt: new Date().toISOString() });
        navigate('/welcome/privacy');
      } else {
        setError(response.error?.message || 'Failed to accept terms');
      }
    } catch {
      setError('An error occurred. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="auth-theme flex min-h-screen items-center justify-center bg-background p-4">
      <div className="w-full max-w-3xl">
        <div className="mb-8 text-center">
          <FileText className="mx-auto mb-4 h-12 w-12 text-primary" />
          <h1 className="font-display text-3xl font-bold tracking-tight text-foreground">
            Terms & Conditions
          </h1>
          <p className="mt-2 text-muted-foreground">
            Please review and accept our terms to continue
          </p>
        </div>

        <AuthCard title="Terms of Service" className="mb-6">
          <div className="h-[450px] overflow-y-auto rounded-md border border-border bg-background p-6">
            <TermsContent showHeading={false} />
          </div>
        </AuthCard>

        {error && <p className="mb-4 text-center text-sm text-destructive">{error}</p>}

        <div className="flex justify-center">
          <Button
            size="lg"
            onClick={handleAccept}
            disabled={isSubmitting}
            className="w-full max-w-md"
          >
            {isSubmitting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Processing...
              </>
            ) : (
              'I Accept the Terms & Conditions'
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}
